import { ArrowRightIcon, CheckIcon, CircleIcon, LockIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  MasteryPathEdge,
  MasteryPathTopic,
  MasteryTopicStatus,
} from '@/lib/coaching/mastery-path'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

const NODE_STYLES: Record<MasteryTopicStatus, string> = {
  mastered: 'border-primary bg-primary text-primary-foreground',
  reinforcing: 'border-primary bg-primary/15 text-foreground',
  learning: 'border-primary bg-card text-foreground',
  // Available to start — track green so open nodes pop at a glance
  available:
    'border-task-exercise bg-task-exercise/10 text-task-exercise shadow-[0_0_10px_-2px] shadow-task-exercise/40',
  locked: 'border-border/60 bg-muted/40 text-muted-foreground/50',
}

const IN_PROGRESS_STATUSES: MasteryTopicStatus[] = ['reinforcing', 'learning']

/** Node diameter for a topic with no subtopics, before the per-subtopic growth. */
const BASE_NODE_SIZE = 34
const NODE_SIZE_PER_SUBTOPIC = 3
const MAX_NODE_SIZE = 64

/** The detail panel each node drives, named so nodes can point at it. */
const DETAIL_PANEL_ID = 'coding-graph-topic-detail'

function GraphNode({
  topic,
  selected,
  onSelect,
}: {
  topic: MasteryPathTopic
  selected: boolean
  onSelect: () => void
}) {
  const { status, masteredCount: done, totalCount: total } = topic
  // Node size scales with subtopic count, capped so a 12-subtopic topic cannot
  // swallow its neighbours the way the fixed 20-node mock never had to handle.
  const size = Math.min(
    MAX_NODE_SIZE,
    BASE_NODE_SIZE + total * NODE_SIZE_PER_SUBTOPIC,
  )
  const progress = total === 0 ? 0 : done / total

  return (
    <div
      className="absolute flex -translate-x-1/2 flex-col items-center gap-1"
      style={{ left: `${topic.x}%`, top: `${topic.y}%` }}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        aria-controls={DETAIL_PANEL_ID}
        aria-label={`${topic.name}: ${done} of ${total} subtopics mastered${status === 'locked' ? ' (locked)' : ''}`}
        className={cn(
          'relative flex items-center justify-center rounded-full border-2 transition-all',
          // Focus is an outline, not a ring: the ring already means "selected"
          // here, and a keyboard user has to be able to tell the two apart.
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          NODE_STYLES[status],
          selected && 'ring-4 ring-ring/40',
          status !== 'locked' && 'hover:scale-110',
        )}
        style={{ width: size, height: size }}
      >
        {status === 'mastered' && (
          <CheckIcon className="size-4" aria-hidden="true" />
        )}
        {IN_PROGRESS_STATUSES.includes(status) && (
          <svg
            viewBox="0 0 36 36"
            className="absolute inset-0 size-full -rotate-90"
            aria-hidden="true"
          >
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              strokeWidth="4"
              className="stroke-primary"
              strokeDasharray={`${progress * 100.5} ${100.5 - progress * 100.5}`}
              strokeLinecap="round"
              pathLength={100.5}
            />
          </svg>
        )}
        {IN_PROGRESS_STATUSES.includes(status) && (
          <span className="text-[10px] font-bold tabular-nums">
            {done}/{total}
          </span>
        )}
        {status === 'locked' && (
          <LockIcon className="size-3.5" aria-hidden="true" />
        )}
        {status === 'available' && (
          <CircleIcon className="size-3" aria-hidden="true" />
        )}
      </button>
      <span
        className={cn(
          'max-w-24 text-center text-[11px] leading-tight font-medium text-balance',
          status === 'locked'
            ? 'text-muted-foreground/50'
            : 'text-foreground/80',
          selected && 'font-semibold text-foreground',
        )}
      >
        {topic.name}
      </span>
    </div>
  )
}

function SubtopicList({
  heading,
  subtopics,
  mastered,
}: {
  heading: string
  subtopics: MasteryPathTopic['subtopics']
  mastered: boolean
}) {
  if (subtopics.length === 0) return null
  return (
    <div className="flex flex-col gap-2">
      <h5 className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
        {heading}
      </h5>
      <ul className="flex flex-col gap-2">
        {subtopics.map((subtopic) => (
          <li key={subtopic.id} className="flex items-center gap-2.5 text-sm">
            <span
              className={cn(
                'flex size-4 shrink-0 items-center justify-center rounded-full border',
                mastered
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-muted/50',
              )}
              aria-hidden="true"
            >
              {mastered && <CheckIcon className="size-3" />}
            </span>
            <Link
              href={`/problems/subtopics/${subtopic.slug}`}
              className={cn(
                'min-w-0 flex-1 truncate hover:underline',
                !mastered && 'text-muted-foreground',
              )}
            >
              {subtopic.name}
            </Link>
            <span className="sr-only">
              {mastered ? '(mastered)' : '(not mastered)'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function TopicDetailCard({ topic }: { topic: MasteryPathTopic }) {
  const mastered = topic.subtopics.filter((subtopic) => subtopic.mastered)
  const remaining = topic.subtopics.filter((subtopic) => !subtopic.mastered)

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-card p-4">
      <div className="flex flex-col gap-1">
        <h4 className="font-semibold">{topic.name}</h4>
        <p className="text-xs text-muted-foreground">
          {topic.masteredCount}/{topic.totalCount} subtopics mastered ·{' '}
          {topic.mastery}% mastery
          {topic.status === 'locked' && topic.blockedBy.length > 0 && (
            <> · needs: {topic.blockedBy.join(', ')}</>
          )}
        </p>
      </div>
      <SubtopicList heading="Mastered" subtopics={mastered} mastered />
      <SubtopicList
        heading="Still to master"
        subtopics={remaining}
        mastered={false}
      />
      <Button variant="outline" size="sm" asChild className="self-start">
        <Link href={`/problems/topics/${topic.slug}`}>
          Practice {topic.name}
          <ArrowRightIcon />
        </Link>
      </Button>
    </div>
  )
}

function GraphLegend() {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-primary" aria-hidden="true" />
        Mastered
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="size-2.5 rounded-full border-2 border-primary bg-primary/15"
          aria-hidden="true"
        />
        Reinforcing
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="size-2.5 rounded-full border-2 border-primary"
          aria-hidden="true"
        />
        Learning
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="size-2.5 rounded-full border-2 border-task-exercise bg-task-exercise/15"
          aria-hidden="true"
        />
        Available
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-muted" aria-hidden="true" />
        Locked
      </span>
    </div>
  )
}

function PathMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
      {children}
    </div>
  )
}

/**
 * The topic the panel opens on: whatever the client is furthest into, so the
 * card lands on live work rather than on a fixed node. Falls back down the
 * status ladder, then to the first topic. That last fallback is positional on
 * purpose: the router orders topics by `displayOrder` then `id` and
 * `buildMasteryPath` keeps that order, so `topics[0]` is the earliest topic in
 * this client's plan, and every tie resolves the same way on every read.
 */
function defaultSelectedTopic(
  topics: MasteryPathTopic[],
): MasteryPathTopic | null {
  const byStatus = (status: MasteryTopicStatus) =>
    topics.find((topic) => topic.status === status)
  return (
    byStatus('reinforcing') ??
    byStatus('learning') ??
    byStatus('available') ??
    topics[0] ??
    null
  )
}

function GraphCanvas({
  topics,
  edges,
  selectedId,
  onSelect,
}: {
  topics: MasteryPathTopic[]
  edges: MasteryPathEdge[]
  selectedId: string | null
  onSelect: (topicId: string) => void
}) {
  const byId = new Map(topics.map((topic) => [topic.id, topic]))
  return (
    <div className="relative h-[560px] min-w-[760px]">
      {/* Prerequisite edges */}
      <svg
        className="absolute inset-0 size-full"
        aria-hidden="true"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        {edges.map((edge) => {
          const from = byId.get(edge.fromTopicId)
          const to = byId.get(edge.toTopicId)
          if (!from || !to) return null
          const midY = (from.y + to.y) / 2
          return (
            <path
              key={`${edge.fromTopicId}-${edge.toTopicId}`}
              d={`M ${from.x} ${from.y + 4} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y - 4}`}
              fill="none"
              vectorEffect="non-scaling-stroke"
              className={cn(
                edge.unlocked ? 'stroke-primary/50' : 'stroke-border',
              )}
              style={{ strokeWidth: edge.unlocked ? 1.5 : 1 }}
            />
          )
        })}
      </svg>

      {topics.map((topic) => (
        <GraphNode
          key={topic.id}
          topic={topic}
          selected={topic.id === selectedId}
          onSelect={() => onSelect(topic.id)}
        />
      ))}
    </div>
  )
}

export function CodingGraph() {
  const query = trpc.dashboard.mastery.path.useQuery()
  // Null until the client picks a node, so the panel follows the data's own
  // default until then instead of latching whatever loaded first.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const detailRef = useRef<HTMLDivElement>(null)

  const selectTopic = (topicId: string) => {
    setSelectedId(topicId)
    // Below `lg` the detail panel sits under a 560px canvas, so a tap on a
    // phone would update something entirely off-screen and read as a no-op.
    if (!window.matchMedia?.('(min-width: 1024px)').matches) {
      detailRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }

  const path = query.data
  if (!path) {
    // The error branch only when there is nothing to show. A background
    // refetch that fails — stale-time window-focus refetch on a flaky
    // connection — must not replace a drawn map and the client's current
    // selection with an error.
    return query.isError ? (
      <PathMessage>
        We could not load your topic map just now. Refresh to try again.
      </PathMessage>
    ) : (
      <Skeleton className="h-[560px] w-full" />
    )
  }

  if (path.state === 'no-client') {
    return (
      <PathMessage>
        Your topic map appears once you are set up with a coach.
      </PathMessage>
    )
  }

  if (path.state === 'not-seeded') {
    return (
      <PathMessage>
        Your coach has not mapped your starting point yet — your topic map fills
        in after your first assessment.
      </PathMessage>
    )
  }

  const selected =
    path.topics.find((topic) => topic.id === selectedId) ??
    defaultSelectedTopic(path.topics)

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="min-w-0 flex-1 overflow-x-auto">
        <GraphCanvas
          topics={path.topics}
          edges={path.edges}
          selectedId={selected?.id ?? null}
          onSelect={selectTopic}
        />
        <GraphLegend />
      </div>

      <div
        ref={detailRef}
        id={DETAIL_PANEL_ID}
        role="region"
        aria-live="polite"
        aria-label={selected ? `${selected.name} detail` : undefined}
        className="w-full shrink-0 lg:w-72"
      >
        {selected && <TopicDetailCard topic={selected} />}
      </div>
    </div>
  )
}
